import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { supabase } from "../supabaseClient";
import TicketReceiptCard, { formatManualTicketId } from "../components/TicketReceiptCard";
import { formatExactSection, resolveStoredSeatArea } from "../lib/seatSectionDisplay";
import { getPaymentServiceBase } from "../lib/paymentApiUrl";
import { resolveEventPolicyHtml } from "../lib/venuePolicyHtml";
import { resolveTicketSectionColor } from "../lib/sectionColor";
import { fetchVenueSectionColors } from "../lib/venuesApi";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const orderId = (searchParams.get("order") || "").trim();
  const [status, setStatus] = useState("confirming");
  const [eventId, setEventId] = useState("");
  const [message, setMessage] = useState("Confirming your payment...");
  const [receipts, setReceipts] = useState([]);
  const [activeReceiptIndex, setActiveReceiptIndex] = useState(0);

  const loadReceipts = useCallback(async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const buyerName = String(user?.user_metadata?.full_name || user?.user_metadata?.name || "").trim();
    const buyerEmail = String(user?.email || "").trim();

    let ticketsQuery = await supabase
      .from("tickets")
      .select("id, qr_code, seat_label, seat_area, ticket_tier_id, is_validated, created_at, attendee_name")
      .eq("payment_order_id", orderId)
      .order("created_at", { ascending: true });

    if (
      ticketsQuery.error &&
      (String(ticketsQuery.error.code || "") === "42703" ||
        String(ticketsQuery.error.message || "").toLowerCase().includes("attendee_name"))
    ) {
      ticketsQuery = await supabase
        .from("tickets")
        .select("id, qr_code, seat_label, seat_area, ticket_tier_id, is_validated, created_at")
        .eq("payment_order_id", orderId)
        .order("created_at", { ascending: true });
    }

    if (ticketsQuery.error && String(ticketsQuery.error.message || "").includes("seat_area")) {
      ticketsQuery = await supabase
        .from("tickets")
        .select("id, qr_code, seat_label, ticket_tier_id, is_validated, created_at")
        .eq("payment_order_id", orderId)
        .order("created_at", { ascending: true });
    }

    const { data: tickets, error } = ticketsQuery;
    if (error || !tickets?.length) {
      return [];
    }

    const tierIds = [...new Set(tickets.map((ticket) => ticket.ticket_tier_id).filter(Boolean))];
    let tiersQuery = await supabase
      .from("ticket_tiers")
      .select("id, event_id, name, price, section, seat_areas, color")
      .in("id", tierIds);

    let tiers = tiersQuery.data;
    if (
      tiersQuery.error &&
      (String(tiersQuery.error.code || "") === "42703" ||
        String(tiersQuery.error.message || "").toLowerCase().includes("color"))
    ) {
      const fallbackTiers = await supabase
        .from("ticket_tiers")
        .select("id, event_id, name, price, section, seat_areas")
        .in("id", tierIds);
      tiers = fallbackTiers.data;
    } else if (tiersQuery.error) {
      tiers = [];
    }

    const eventIds = [...new Set((tiers || []).map((tier) => tier.event_id).filter(Boolean))];
    let events = [];
    if (eventIds.length) {
      let eventsQuery = await supabase
        .from("events")
        .select("id, title, date, time, venue, address, venue_policy_snapshot, description, venue_id, seat_layout_snapshot")
        .in("id", eventIds);
      if (eventsQuery.error) {
        eventsQuery = await supabase
          .from("events")
          .select("id, title, date, time, venue")
          .in("id", eventIds);
      }
      events = eventsQuery.data || [];
    }

    const venueSections = await fetchVenueSectionColors(
      supabase,
      (events || []).map((event) => event.venue_id)
    );

    const tiersById = Object.fromEntries((tiers || []).map((tier) => [tier.id, tier]));
    const eventsById = Object.fromEntries((events || []).map((event) => [event.id, event]));

    return tickets.map((ticket) => {
      const tier = tiersById[ticket.ticket_tier_id] || {};
      const event = eventsById[tier.event_id] || {};
      const seatArea = resolveStoredSeatArea({
        seatArea: ticket.seat_area,
        seatLabel: ticket.seat_label,
        tierSeatAreas: tier.seat_areas,
      });
      return {
        id: ticket.id,
        qrCode: ticket.qr_code || "",
        manualTicketId: formatManualTicketId(ticket.id),
        seatLabel: ticket.seat_label || "",
        attendeeName: String(ticket.attendee_name || "").trim() || buyerName || buyerEmail || "Guest",
        attendeeEmail: buyerEmail || "-",
        tierName: tier.name || "General Admission",
        price: Number(tier.price || 0),
        sectionDisplay: formatExactSection(tier.section || tier.name, seatArea),
        sectionColor: resolveTicketSectionColor({
          tier,
          event,
          venueSections,
        }),
        statusLabel: ticket.is_validated ? "Used" : "Active",
        eventTitle: event.title || "Event",
        eventDate: event.date || "",
        eventTime: event.time || "",
        eventVenue: event.venue || "-",
        eventAddress: event.address || "",
        policyHtml: resolveEventPolicyHtml(event),
        eventId: event.id || "",
      };
    });
  }, [orderId]);

  useEffect(() => {
    if (!orderId) {
      setStatus("error");
      setMessage("Missing payment order. If you were charged, contact support with your receipt.");
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      attempts += 1;
      try {
        const response = await fetch(
          `${getPaymentServiceBase()}/checkout-status?order=${encodeURIComponent(orderId)}`
        );
        const result = await response.json();
        if (cancelled) return;

        if (result.event_id) setEventId(result.event_id);

        if (result.status === "paid") {
          const nextReceipts = await loadReceipts();
          if (cancelled) return;
          setReceipts(nextReceipts);
          setStatus("paid");
          setMessage(
            nextReceipts.length > 0
              ? "Payment confirmed. Your tickets have been issued."
              : "Payment confirmed. Your tickets will appear in My Tickets shortly."
          );
          return;
        }

        if (result.status === "failed") {
          setStatus("failed");
          setMessage(result.error || "Payment was received but tickets could not be issued. Contact support.");
          return;
        }

        if (result.status === "cancelled") {
          setStatus("failed");
          setMessage("This payment was cancelled.");
          return;
        }
      } catch (error) {
        console.error("Unable to confirm payment status:", error);
      }

      if (attempts >= maxAttempts) {
        if (!cancelled) {
          setStatus("pending");
          setMessage("We are still confirming this payment. Check My Tickets in a moment, or contact support if nothing appears.");
        }
        return;
      }

      window.setTimeout(poll, 2000);
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [orderId, loadReceipts]);

  const isError = status === "error" || status === "failed";
  const activeReceipt = receipts[activeReceiptIndex] || null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] px-6 py-10 text-white">
      <div className="w-full max-w-md bg-[#161616] p-8 rounded-3xl border border-white/10 text-center">
        <div
          className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
            isError ? "bg-red-500/10 text-red-400" : status === "paid" ? "bg-emerald-500/10 text-emerald-400" : "bg-white/5 text-white/70"
          }`}
        >
          {status === "confirming" ? (
            <Loader2 size={40} className="animate-spin" />
          ) : isError ? (
            <AlertCircle size={40} />
          ) : (
            <CheckCircle2 size={40} />
          )}
        </div>

        <p className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-2">PayMongo</p>
        <h1 className="text-2xl font-black mb-4 tracking-tight">
          {status === "paid" ? "Payment successful" : status === "confirming" ? "Confirming payment" : "Payment update"}
        </h1>
        <p className="text-sm text-white/70 mb-6">{message}</p>

        {activeReceipt ? (
          <div className="text-left mb-6">
            {receipts.length > 1 && (
              <p className="text-center text-xs text-white/50 mb-3">
                Ticket {activeReceiptIndex + 1} of {receipts.length}
              </p>
            )}
            <TicketReceiptCard ticket={activeReceipt} />
            {receipts.length > 1 && (
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  disabled={activeReceiptIndex === 0}
                  onClick={() => setActiveReceiptIndex((prev) => Math.max(0, prev - 1))}
                  className="flex-1 border border-white/20 py-2 rounded-full text-[10px] font-black uppercase tracking-widest disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={activeReceiptIndex === receipts.length - 1}
                  onClick={() => setActiveReceiptIndex((prev) => Math.min(receipts.length - 1, prev + 1))}
                  className="flex-1 border border-white/20 py-2 rounded-full text-[10px] font-black uppercase tracking-widest disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <Link
            to="/tickets"
            className="w-full bg-white text-black py-3 rounded-full text-xs font-black uppercase tracking-widest"
          >
            View my tickets
          </Link>
          {(eventId || activeReceipt?.eventId) && (
            <Link
              to={`/event/${eventId || activeReceipt.eventId}`}
              className="w-full border border-white/20 py-3 rounded-full text-xs font-black uppercase tracking-widest"
            >
              Back to event
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
